/**
 * Pure reducer: chat/agent operation events → the in-progress reply.
 * The saved conversation (DB, fetched via TanStack Query) is the source of
 * truth; this state only lives while the reply streams.
 */

import type { ContextInfoData, StreamEvent, ToolConfirmationData } from "@/api";
import { type DraftView, draftFromEvent } from "./drafts/draftModel";
import { type ResearchRunView, withResearchProgress } from "./research/researchModel";

export interface ToolCallView {
  id: string;
  name: string;
  status: "running" | "done" | "error";
}

export interface SourceView {
  type?: string;
  name?: string;
  url?: string;
}

/** An extension permission the user turned off that the reply needed (backend `request_extension_access`). */
export interface ExtensionAccessRequest {
  slug: string;
  name: string;
  permission: "read" | "write";
}

/** A per-person connector the reply needed but the person hasn't connected (phase 4, MHS-822). */
export interface ConnectorConnectRequest {
  uuid: string;
  name: string;
  /** "oauth": the person signs in to the server (phase 6) rather than pasting a token. */
  authMode?: string;
}

export interface PausedReply {
  reason: "max_iterations" | "rate_limited";
  steps: number | null;
}

export function pauseOf(metadata: Record<string, unknown> | null | undefined): PausedReply | null {
  const reason =
    metadata?.stop_reason === "rate_limited" || metadata?.rate_limited === true
      ? "rate_limited"
      : metadata?.stop_reason === "max_iterations" || metadata?.max_iterations_reached === true
        ? "max_iterations"
        : null;
  if (!reason) return null;
  const limit = metadata?.max_iterations;
  return {
    reason,
    steps:
      reason === "max_iterations" && typeof limit === "number" && Number.isInteger(limit) && limit > 0 ? limit : null,
  };
}

export interface ChatStreamState {
  text: string;
  tools: ToolCallView[];
  sources: SourceView[];
  /** The model asked the user something and stopped. */
  askUser: { question: string; questions: unknown[] } | null;
  usage: { tokens?: number; costUsd?: number } | null;
  /** User-facing error message from the backend, if the reply failed. */
  error: { message: string; code?: string | null } | null;
  stopped: boolean;
  /** Build with AI drafts proposed in this reply, latest version of each. */
  drafts: DraftView[];
  /** The reply needed an extension the user turned off; the chat offers to allow it and retry. */
  extensionAccess: ExtensionAccessRequest | null;
  /** The reply needed a connector that takes my own token; the chat offers to connect and retry. */
  connectorConnect: ConnectorConnectRequest | null;
  /** The reply is waiting out a provider rate limit before retrying (MHS-715). */
  capacityWait: { retryAfterSeconds: number | null } | null;
  pause: PausedReply | null;
  /** The latest context-window report (sent each tool step); shown under the composer when the user opts in. */
  context: ContextInfoData | null;
  /**
   * The tool call this reply proposed (`tool_confirmation`, MHS-795), as sent: each tool's card reads its own (the
   * research card shows until the saved reply takes over).
   */
  confirmation: ToolConfirmationData | null;
  /** This reply's research runs by tool call id, each at its latest `research_progress` (MHS-798). */
  research: Record<string, ResearchRunView>;
}

export const initialChatStream: ChatStreamState = {
  text: "",
  tools: [],
  sources: [],
  askUser: null,
  usage: null,
  error: null,
  stopped: false,
  drafts: [],
  extensionAccess: null,
  connectorConnect: null,
  capacityWait: null,
  pause: null,
  context: null,
  confirmation: null,
  research: {},
};

function sourcesFrom(metadata: Record<string, unknown> | null | undefined): SourceView[] {
  const raw = metadata?.sources;
  return Array.isArray(raw) ? (raw as SourceView[]) : [];
}

function connectorConnectFrom(metadata: Record<string, unknown> | null | undefined): ConnectorConnectRequest | null {
  const raw = metadata?.connector_connect as (Partial<ConnectorConnectRequest> & { auth_mode?: unknown }) | undefined;
  if (!raw || typeof raw.uuid !== "string" || typeof raw.name !== "string") return null;
  return typeof raw.auth_mode === "string"
    ? { uuid: raw.uuid, name: raw.name, authMode: raw.auth_mode }
    : { uuid: raw.uuid, name: raw.name };
}

function extensionAccessFrom(metadata: Record<string, unknown> | null | undefined): ExtensionAccessRequest | null {
  const raw = metadata?.extension_access as Partial<ExtensionAccessRequest> | undefined;
  if (!raw || typeof raw.slug !== "string" || typeof raw.name !== "string") return null;
  if (raw.permission !== "read" && raw.permission !== "write") return null;
  return { slug: raw.slug, name: raw.name, permission: raw.permission };
}

export function chatStreamReducer(state: ChatStreamState, event: StreamEvent): ChatStreamState {
  return withCapacityWait(applyEvent(state, event), event);
}

/** Set by a ``waiting_for_capacity`` context_info; anything else that means the reply moved on (the
 *  clearing context_info, new text or tools, the end of the reply) clears it. */
function withCapacityWait(state: ChatStreamState, event: StreamEvent): ChatStreamState {
  if (event.event === "context_info") {
    const waiting = event.data.status === "waiting_for_capacity";
    return { ...state, capacityWait: waiting ? { retryAfterSeconds: event.data.retry_after_seconds ?? null } : null };
  }
  if (state.capacityWait && event.event !== "message_start") return { ...state, capacityWait: null };
  return state;
}

function applyEvent(state: ChatStreamState, event: StreamEvent): ChatStreamState {
  switch (event.event) {
    case "content_delta":
      return { ...state, text: state.text + event.data.delta };
    case "tool_start":
      return {
        ...state,
        tools: [...state.tools, { id: event.data.tool_call_id, name: event.data.name, status: "running" }],
      };
    case "tool_end":
      return {
        ...state,
        tools: state.tools.map((t) =>
          t.id === event.data.tool_call_id ? { ...t, status: event.data.is_error ? "error" : "done" } : t,
        ),
        sources: [...state.sources, ...sourcesFrom(event.data.metadata)],
        extensionAccess: extensionAccessFrom(event.data.metadata) ?? state.extensionAccess,
        connectorConnect: connectorConnectFrom(event.data.metadata) ?? state.connectorConnect,
      };
    case "draft_updated": {
      const draft = draftFromEvent(event.data);
      const known = state.drafts.some((d) => d.draftId === draft.draftId);
      return {
        ...state,
        drafts: known ? state.drafts.map((d) => (d.draftId === draft.draftId ? draft : d)) : [...state.drafts, draft],
      };
    }
    case "context_info":
      // A capacity-wait report carries no token counts, so it leaves the last real one in place.
      return event.data.prompt_tokens == null ? state : { ...state, context: event.data };
    case "ask_user":
      return { ...state, askUser: { question: event.data.question ?? "", questions: event.data.questions ?? [] } };
    case "tool_confirmation":
      return { ...state, confirmation: event.data };
    case "research_progress":
      return { ...state, research: withResearchProgress(state.research, event.data) };
    case "message_end":
      return {
        ...state,
        pause: pauseOf(event.data),
        usage: { tokens: event.data.tokensUsed ?? undefined, costUsd: event.data.costUsd ?? undefined },
        stopped: state.stopped || Boolean(event.data.cancelled),
      };
    case "error":
      return { ...state, error: { message: event.data.message, code: event.data.code } };
    case "operation_failed":
      return state.error ? state : { ...state, error: { message: event.data.error } };
    case "operation_completed":
      return event.data.cancelled ? { ...state, stopped: true } : state;
    default:
      // message_start, route_decision, lifecycle: nothing to show yet.
      return state;
  }
}
